'use client';

import type { Place, SelectOption } from '@/types';
import {
  ActionBarWrapper,
  Button,
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui';
import {
  IconCalendarEventFilled,
  IconCarouselHorizontalFilled,
  IconLayoutGrid,
  IconListDetails,
  IconX,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { useMediaQuery } from 'usehooks-ts';

import { useEvents } from '@/lib/hooks/use-events';

const viewIcons = {
  timeline: IconListDetails,
  calendar: IconLayoutGrid,
  'carousel-large': IconCarouselHorizontalFilled,
  'carousel-small': IconCarouselHorizontalFilled,
};

export function EventSearchActionBar({
  placesData,
  views,
  view,
  setView,
  availableFilters,
  eventsData,
}: {
  placesData: Place[] | undefined;
  views: SelectOption[];
  view: string;
  setView: (view: string) => void;
  availableFilters: SelectOption[];
  eventsData: ReturnType<typeof useEvents>;
}) {
  const {
    categories,
    category,
    cities,
    city,
    clearCategory,
    clearCity,
    clearPlace,
    fromDate,
    maxDate,
    place,
    places,
    setFromDate,
    setCategory,
    setCity,
    setPlace,
  } = eventsData;
  const mobile = useMediaQuery('(max-width: 640px)');

  const wrapperStyles = 'flex w-full min-w-max items-center relative';
  const selectStyles = 'h-14 bg-transparent w-full min-w-max items-center justify-between gap-3 border-none';

  return (
    <ActionBarWrapper className="-mt-4 w-[calc(100%-2rem)] flex-col overflow-clip sm:-mt-8 md:w-max md:flex-row">
      {/* Date range */}
      {availableFilters?.some((filter) => filter.value === 'date') && (
        <div className="flex w-full flex-col">
          <Label className="sr-only">Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="group h-14 justify-between pr-3 text-sm!">
                Events from {format(fromDate, 'do MMMM yyyy')}
                <IconCalendarEventFilled className="text-muted-foreground group-hover:text-accent-foreground size-4" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[calc(100vw-2rem)] p-0 sm:w-auto">
              <Calendar
                mode="single"
                required
                disabled={[{ before: new Date(), after: maxDate }]}
                numberOfMonths={mobile ? 1 : 2}
                selected={fromDate}
                onSelect={setFromDate}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Category */}
      {availableFilters?.some((filter) => filter.value === 'category') && categories.length > 1 && (
        <div className={wrapperStyles}>
          <Label className="sr-only">Category</Label>
          <Select value={String(category || '')} onValueChange={setCategory}>
            <SelectTrigger className={selectStyles}>
              <SelectValue placeholder="All categories" />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                <SelectLabel>Categories</SelectLabel>
                <SelectSeparator />
                {categories.map(({ value, count }) => (
                  <SelectItem className="group" key={value} value={value}>
                    <span className="mr-8 w-full">{value}</span>
                    <span className="text-muted-foreground group-hover:text-accent-foreground group-focus:text-accent-foreground">
                      {count}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {category && (
            <Button
              variant="ghost"
              size="icon"
              className="bg-background absolute right-2 rounded-sm"
              onClick={() => clearCategory()}
            >
              <IconX className="size-4" />
            </Button>
          )}
        </div>
      )}

      {/* City */}
      {availableFilters?.some((filter) => filter.value === 'city') &&
        (!place || city) &&
        Boolean(cities?.length && cities?.length > 1) && (
          <div className={wrapperStyles}>
            <Label className="sr-only">City</Label>
            <Select value={String(city || '')} onValueChange={setCity}>
              <SelectTrigger className={selectStyles}>
                <SelectValue placeholder="All cities" />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Cities</SelectLabel>
                  <SelectSeparator />
                  {cities?.map(({ value, count }) => (
                    <SelectItem className="group" key={value} value={value}>
                      <span className="mr-8 w-full">{value}</span>
                      <span className="text-muted-foreground group-hover:text-accent-foreground group-focus:text-accent-foreground">
                        {count}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {city && (
              <Button
                variant="ghost"
                size="smIcon"
                className="bg-background absolute right-0.5 rounded-sm"
                onClick={clearCity}
              >
                <IconX className="size-4" />
              </Button>
            )}
          </div>
        )}

      {/* Place */}
      {availableFilters?.some((filter) => filter.value === 'venue') && !city && places.length > 1 && (
        <div className={wrapperStyles}>
          <Label className="sr-only">Place</Label>
          <Select value={String(place || '')} onValueChange={setPlace}>
            <SelectTrigger className={selectStyles}>
              <SelectValue placeholder="All venues" />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                <SelectLabel>Venues</SelectLabel>
                <SelectSeparator />
                {places.map(({ value, count }) => (
                  <SelectItem className="group" key={value} value={value}>
                    <span className="mr-8 w-full">
                      {placesData?.find((p) => p.id === value)?.custom.displayName || value}
                    </span>
                    <span className="text-muted-foreground group-hover:text-accent-foreground group-focus:text-accent-foreground">
                      {count}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {place && (
            <Button
              variant="ghost"
              size="smIcon"
              className="bg-background absolute right-0.5 rounded-sm"
              onClick={() => {
                clearPlace();
              }}
            >
              <IconX className="size-4" />
            </Button>
          )}
        </div>
      )}

      {/* View */}
      {views?.length > 1 && (
        <TooltipProvider>
          <ToggleGroup className="w-full" type="single" value={String(view || '')} onValueChange={setView}>
            {views.map(({ value, label }) => {
              const Icon = viewIcons[value as keyof typeof viewIcons];

              return (
                <Tooltip key={value}>
                  <TooltipTrigger asChild>
                    <div className="w-full">
                      <ToggleGroupItem size="lg" className="h-14 w-full gap-2 lg:w-14" key={value} value={value}>
                        <Icon className="size-4 shrink-0 sm:size-5" />
                        {mobile && label}
                      </ToggleGroupItem>
                    </div>
                  </TooltipTrigger>

                  {!mobile && <TooltipContent>{label}</TooltipContent>}
                </Tooltip>
              );
            })}
          </ToggleGroup>
        </TooltipProvider>
      )}
    </ActionBarWrapper>
  );
}
